import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Link,
} from '@mui/material'
import { Edit, Delete, ShoppingCart, Warning } from '@mui/icons-material'
import type { Supply } from '@/types'

interface SupplyCardProps {
  supply: Supply
  onEdit: (supply: Supply) => void
  onDelete: (id: string) => void
}

export default function SupplyCard({ supply, onEdit, onDelete }: SupplyCardProps) {
  const isLowStock = supply.quantity <= supply.min_quantity
  const stockPercentage = (supply.quantity / (supply.min_quantity * 2)) * 100

  const getStockColor = () => {
    if (isLowStock) return '#ef4444'
    if (stockPercentage > 75) return '#10b981'
    return '#f59e0b'
  }

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${getStockColor()} 0%, ${getStockColor()}99 100%)`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                }}
              >
                {supply.name}
              </Typography>
              {isLowStock && (
                <Chip
                  icon={<Warning sx={{ fontSize: '0.9rem !important' }} />}
                  label="Low Stock"
                  size="small"
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Box>

            <Chip
              label={supply.category}
              size="small"
              sx={{
                mb: 2,
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />

            <Box mb={2.5}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                  }}
                >
                  Stock Level
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: getStockColor(),
                    fontSize: '0.875rem',
                  }}
                >
                  {supply.quantity} / {supply.min_quantity} min
                </Typography>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  height: 12,
                  bgcolor: 'rgba(0,0,0,0.06)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${Math.min(stockPercentage, 100)}%`,
                    background: `linear-gradient(90deg, ${getStockColor()} 0%, ${getStockColor()}cc 100%)`,
                    borderRadius: 2,
                    transition: 'width 0.3s ease-in-out',
                  }}
                />
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              {supply.cost && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  💰 ${supply.cost.toFixed(2)}
                </Typography>
              )}

              {supply.purchase_link && (
                <Link
                  href={supply.purchase_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.08)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(99, 102, 241, 0.15)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ShoppingCart fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Purchase
                  </Typography>
                </Link>
              )}
            </Box>
          </Box>

          <Box display="flex" gap={1} ml={2}>
            <IconButton
              size="medium"
              onClick={() => onEdit(supply)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.1) rotate(5deg)',
                },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => onDelete(supply.id)}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'error.dark',
                  transform: 'scale(1.1) rotate(-5deg)',
                },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
